import config from '@/config/user.env';
import bigLogData from '@/utils/bigLogData';
import { useStore } from 'vuex';

/**
 * 菜单点击事件的参数接口
 */
interface MenuInfo {
  path?: { frontUrl: string; resourceName: string }[];
  menusList: string[];
}

/** microLsp 下嵌套子应用路径与系统标识映射 */
const nestedMicroLspWebSystemMap = [
  { prefix: '/microLsp/app-cci', webSystem: 'CCI' },
  { prefix: '/microLsp/report', webSystem: 'REPORT' },
  { prefix: '/microLsp/outPage', webSystem: 'AMS' },
  { prefix: '/microLsp/bmsMicroApp', webSystem: 'BMS' },
  { prefix: '/microLsp/app-cop', webSystem: 'COP' },
];

const webSystemMap: Record<string, string> = {
  microOms: 'OMS',
  microTms: 'TMS',
  microDataReport: 'el-data-report',
  microLsp: 'LSP',
  microLsrm: 'LSRM',
  microNtp: 'NTP',
  microCsp: 'CSP',
  microEam: 'EAM',
  microPrivacy: 'PRIVACY',
  microReport: 'REPORT',
  microAms: 'AMS',
  microCop: 'COP',
  microCci: 'CCI',
  microBms: 'BMS',
  microDc: 'DC',
};

/** 根据路由路径获取子应用系统标识（webSystem） */
export const getWebSystemByPath = (path: string): string => {
  for (const entry of nestedMicroLspWebSystemMap) {
    if (path.startsWith(entry.prefix)) {
      return entry.webSystem;
    }
  }
  const hashName = path.split('/')[1];
  return webSystemMap[hashName] || 'EL';
};

/** microLsp 下嵌套子应用路径与子模块名称映射 */
const nestedMicroLspPathMap = [
  { prefix: '/microLsp/app-cci', moduleName: config.cciChildModuleName },
  { prefix: '/microLsp/report', moduleName: config.reportChildModuleName },
  { prefix: '/microLsp/outPage', moduleName: config.amsChildModuleName },
  { prefix: '/microLsp/bmsMicroApp', moduleName: config.bmsChildModuleName },
  { prefix: '/microLsp/app-cop', moduleName: config.copChildModuleName },
];

const appendRoleName = (childModuleName: string, roleName: string): string => {
  if (roleName.length > 0 && childModuleName.length > 0) {
    return `${childModuleName}-${roleName}`;
  }
  return childModuleName;
};

/**
 * 根据hash路由名称获取对应的子模块名称
 * @param hashName - 路由hash名称
 * @returns 对应的子模块名称
 */
export const getChildModuleName = (hashName: string): string => {
  switch (hashName) {
    case 'microOms':
      return config.otpChildModuleName as string;
    case 'microTms':
      return config.otpChildModuleName as string;
    case 'microDataReport':
      return config.otpChildModuleName as string;
    case 'microLsp':
      return config.lspChildModuleName as string;
    case 'microNtp':
      return config.ntpChildModuleName as string;
    case 'microLsrm':
      return config.lsrmChildModuleName as string;
    case 'microCop':
      return config.copChildModuleName as string;
    case 'microCci':
      return config.cciChildModuleName as string;
    case 'microAms':
      return config.amsChildModuleName as string;
    case 'microBms':
      return config.bmsChildModuleName as string;
    case 'microPrivacy':
      return config.privacyChildModuleName as string;
    case 'microReport':
      return config.reportChildModuleName as string;
    case 'microCsp':
      return config.cspChildModuleName as string;
    case 'microEam':
      return config.eamChildModuleName as string;
    case 'microDc':
      return config.dcChildModuleName as string;
    default:
      return '';
  }
};
const externalMicroUrlMap = [
  { prefix: config.microEamUrl, moduleName: config.eamChildModuleName, webSystem: 'EAM' },
  { prefix: config.microAmsUrl, moduleName: config.amsChildModuleName, webSystem: 'AMS' },
  { prefix: config.microCspUrl, moduleName: config.cspChildModuleName, webSystem: 'CSP' },
  { prefix: config.microCciUrl, moduleName: config.cciChildModuleName, webSystem: 'CCI' },
  { prefix: config.microReportUrl, moduleName: config.reportChildModuleName, webSystem: 'REPORT' },
  { prefix: config.microCopUrl, moduleName: config.copChildModuleName, webSystem: 'COP' },
  { prefix: config.microBmsUrl, moduleName: config.bmsChildModuleName, webSystem: 'BMS' },
  { prefix: config.microPrivacyUrl, moduleName: config.privacyChildModuleName, webSystem: 'PRIVACY' },
];

// 根据url获取对应的子模块名称
const getChildModuleNameWithHttpUrl = (url: string): string => {
  for (const entry of externalMicroUrlMap) {
    if (entry.prefix && url.startsWith(entry.prefix)) {
      return entry.moduleName ?? '';
    }
  }
  return '';
};

const getWebSystemByHttpUrl = (url: string): string => {
  for (const entry of externalMicroUrlMap) {
    if (entry.prefix && url.startsWith(entry.prefix)) {
      return entry.webSystem;
    }
  }
  return 'EL';
};

/**
 * 根据路由路径获取子模块名称，并拼接当前角色名称
 * @param path - 路由路径
 * @param roleName - 当前角色名称
 */
export const getChildModuleNameByPath = (path: string, roleName = ''): string => {
  for (const entry of nestedMicroLspPathMap) {
    if (path.startsWith(entry.prefix)) {
      return appendRoleName(entry.moduleName ?? '', roleName);
    }
  }
  const hashName = path.split('/')[1];
  return appendRoleName(getChildModuleName(hashName), roleName);
};

// 基座统一埋点：排除无需统计的路径；已自建埋点的子应用由子系统上报，基座不再重复统计
const STATISTIC_EXCLUDED_PATH_PREFIXES = ['/login', '/401', '/404', '/redirect', '/h5/'];

/** microLsp 嵌套子应用由基座统计（LSP 主应用自身不上报这些路径） */
const BASE_TRACKED_NESTED_LSP_PREFIXES = [
  '/microLsp/app-cci',
  '/microLsp/report',
  '/microLsp/outPage',
  '/microLsp/bmsMicroApp',
  '/microLsp/app-cop',
];

/** 子应用已接入自建页面埋点（参照 OTP router.beforeEach），基座不再重复统计 */
const SELF_TRACKING_MICRO_APPS = new Set([
  // 'microOms',
  // 'microTms',
  'microDataReport',
  // 'microOtp',
  'microLsp',
  'microNtp',
  'microLsrm',
]);

export const statisticIfNeed = (url: string): boolean => {
  if (!url || url === '/') {
    return false;
  }
  // return !STATISTIC_EXCLUDED_PATH_PREFIXES.some((prefix) => url.startsWith(prefix));
  if (STATISTIC_EXCLUDED_PATH_PREFIXES.some((prefix) => url.startsWith(prefix))) {
    return false;
  }
  if (BASE_TRACKED_NESTED_LSP_PREFIXES.some((prefix) => url.startsWith(prefix))) {
    return true;
  }
  const hashName = url.split('/')[1];
  if (SELF_TRACKING_MICRO_APPS.has(hashName)) {
    return false;
  }
  return true;
};

/**
 * 菜单点击事件的Hook
 * @returns {Object} 包含menuClick方法的对象
 */
export const useMenuClick = () => {
  // 提取当前角色
  const store = useStore();
  const { curRole, roleList } = store.state;
  const roleItem = roleList.find((item: { roleCode: any }) => item.roleCode === curRole);
  // 提取当前角色name
  const roleName = roleItem?.roleName ?? '';
  const menuClick = (info: MenuInfo): void => {
    const lastPath = info?.path?.[info.path.length - 1];
    if (lastPath?.frontUrl) {
      const url = lastPath.frontUrl;
      let childModuleName = '';
      if (url.startsWith('http')) {
        // const hashName = window.location.hash.split('/')[1];
        childModuleName = appendRoleName(getChildModuleNameWithHttpUrl(url), roleName);
      }else {
        return;
        // 改为路由router.beforeEach(to, from, next)中处理
        // const hashName = url.split('/')[1];
        // if(statisticIfNeed(url) === false){
        //   return;
        // }
        // childModuleName = getChildModuleName(hashName);
      }
      const params: { [key: string]: string } = {};
      info.menusList.forEach((name, index) => {
        params['model_name' + (index + 1)] = name;
        if (index === 0 && childModuleName.length === 0) {
          childModuleName = appendRoleName(`安得数字化运营平台-${name}`, roleName);
        }
      });
      const webSystem = getWebSystemByHttpUrl(url);
      bigLogData.sendDetailMessage({
        modelInfo: params,
        moduleName: 'C-EL',
        childModuleName,
        href: url,
        webSystem,
        webVersion: webSystem === 'EL' ? config.webVersion || '' : '',
      });
    }
  };

  return {
    menuClick,
    statisticIfNeed,
    getChildModuleName,
    getChildModuleNameByPath,
    getWebSystemByPath,
  };
};
